import { useMemo, useRef, useState, useEffect } from 'react'
import Paper from '@/components/Paper'
import PieChart, { PieChartDataItem } from '@/components/Analytic/PieChart'
import { SCHEMES } from '@/components/EchartCustomized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getEmployeeDashboard } from '@/modules/Report/services'
import { SalesTargetFilters } from '@/modules/Report/components/BusinessTargets/types'
import { formatVND } from '@/modules/Report/components/TargetAnalytics/helpers'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

interface EmployeeSalesContributionsProps {
    filters: SalesTargetFilters
}

export default function EmployeeSalesContributions({
    filters,
}: EmployeeSalesContributionsProps) {
    const { t } = useTranslation('report')
    const containerRef = useRef<HTMLDivElement>(null)
    const [chartHeight, setChartHeight] = useState(400)

    // Fetch dashboard data
    const { data: dashboardData, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.EMPLOYEE_DASHBOARD,
            'employee-contributions',
            filters,
        ],
        queryFn: () => getEmployeeDashboard(filters),
        enabled:
            !!filters.employee_id &&
            filters.employee_id.length > 0 &&
            !!filters.gte &&
            !!filters.lte,
    })

    useEffect(() => {
        if (!containerRef.current) return
        const observer = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid ResizeObserver loop warning
            requestAnimationFrame(() => {
                for (const entry of entries) {
                    if (entry.contentRect) {
                        setChartHeight(
                            Math.max(entry.contentRect.height * 0.8, 400)
                        )
                    }
                }
            })
        })
        observer.observe(containerRef.current)
        return () => {
            observer.disconnect()
        }
    }, [])

    // Transform dashboard data to pie chart format
    const chartData: PieChartDataItem[] = useMemo(() => {
        if (!dashboardData?.employees || dashboardData.employees.length === 0) {
            return []
        }

        return dashboardData.employees
            .filter((emp) => emp.actual_revenue > 0)
            .map((emp, index) => ({
                name: emp.employee_name,
                value: emp.actual_revenue,
                color: SCHEMES.default[index % SCHEMES.default.length],
            }))
    }, [dashboardData])

    const hasData = chartData.length > 0

    return (
        <div ref={containerRef} className="h-full w-full">
            <Paper className="h-full flex flex-col">
                {/* Header */}
                <Typogaphy className="text-sm font-medium text-[#1A237E]">
                    {t('target_analytics.employee_sales_contribution.title')}
                </Typogaphy>

                {/* Loading/Error State */}
                {renderState()}

                {/* Empty State */}
                {!renderState() && !hasData && (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-xs text-gray-400 italic">
                            {t(
                                'target_analytics.employee_sales_contribution.filter_prompt'
                            )}
                        </p>
                    </div>
                )}

                {/* Chart Section */}
                {!renderState() && hasData && (
                    <div>
                        <PieChart
                            data={chartData}
                            height={chartHeight}
                            center={['50%', '40%']}
                            legendPosition="bottom"
                            tooltipFormatter={({ name, value, percent }) =>
                                `<div style="padding: 8px;">
                                <div style="font-weight: 600; margin-bottom: 4px; text-transform: capitalize;">${name}</div>
                                <div>${formatVND(value)} (${percent.toFixed(2)}%)</div>
                            </div>`
                            }
                            labelFormatter={({ name, percent }) =>
                                `${name} {percent|${percent.toFixed(2)}%}`
                            }
                            labelRich={{
                                percent: {
                                    color: 'inherit',
                                },
                            }}
                            legendIcon="circle"
                            legendItemSize={6}
                            legendType="plain"
                            legendGap={0}
                        />
                    </div>
                )}
            </Paper>
        </div>
    )
}
